import { useEffect, useMemo, useRef, useState } from "react";

import { useTable } from "@tanstack/react-table-v9";
import type {
  ReactTable,
  RowData,
  SortingState,
  Updater,
} from "@tanstack/react-table-v9";

import type { TableV2ColumnDef } from "../columns";
import { EXPANDER_COLUMN_ID, SELECTION_COLUMN_ID } from "../columns";
import { tableV2Features } from "../features";
import type { TableV2Features } from "../features";
import {
  buildPivotColumns,
  PIVOT_LABEL_COLUMN_ID,
  pivotSortingState,
  toPivotSort,
} from "../pivot/columns";
import type { PivotCellClick } from "../pivot/columns";
import { pivotDetailRows } from "../pivot/engine";
import type {
  PivotConfig,
  PivotField,
  PivotRowNode,
  PivotResult,
} from "../pivot/types";
import { usePivotController } from "../pivot/usePivotController";
import type {
  PivotController,
  PivotFetchRequest,
} from "../pivot/usePivotController";

export type TableV2View = "table" | "pivot";

export interface TableV2PivotOptions<TData> {
  initialConfig: PivotConfig;
  /** Defaults to fields derived from the table's columns */
  fields?: PivotField<TData>[];
  /** Django pivot endpoint, used in server mode or above `clientRowLimit` rows */
  fetchPivot?: (request: PivotFetchRequest) => Promise<PivotResult>;
  clientRowLimit?: number;
  initiallyExpanded?: "none" | "all";
  initialView?: TableV2View;
  onCellClick?: (cell: PivotCellClick) => void;
  drillToDetail?:
    | boolean
    | ((cell: PivotCellClick, config: PivotConfig) => Promise<TData[]>);
}

export interface TableV2PivotDetail<TData> {
  cell: PivotCellClick;
  loadRows: () => Promise<TData[]>;
  close: () => void;
}

export interface TableV2PivotView<TData> {
  controller: PivotController<TData>;
  table: ReactTable<TableV2Features, PivotRowNode>;
  drillToDetail: boolean;
  detail: TableV2PivotDetail<TData> | null;
}

const EMPTY_CONFIG: PivotConfig = { rows: [], columns: [], values: [] };
const NO_COLUMNS: TableV2ColumnDef<PivotRowNode>[] = [];

const pathValue = (row: unknown, path: string) =>
  path
    .split(".")
    .reduce<unknown>(
      (value, key) => (value as Record<string, unknown> | null)?.[key],
      row,
    );

export const fieldsFromColumns = <TData extends RowData>(
  columns: TableV2ColumnDef<TData>[],
): PivotField<TData>[] => {
  const fields: PivotField<TData>[] = [];
  const visit = (defs: TableV2ColumnDef<TData>[]) =>
    defs.forEach((def) => {
      const nested = (def as { columns?: TableV2ColumnDef<TData>[] }).columns;
      if (nested) {
        visit(nested);
        return;
      }
      const accessorKey = (def as { accessorKey?: string }).accessorKey;
      const accessorFn = (
        def as { accessorFn?: (row: TData, index: number) => unknown }
      ).accessorFn;
      const id = def.id ?? accessorKey;
      const meta = def.meta;
      if (!id || id === SELECTION_COLUMN_ID || id === EXPANDER_COLUMN_ID)
        return;
      if (meta?.type === "actions" || (!accessorKey && !accessorFn)) return;
      const type =
        meta?.type === "date" || meta?.type === "datetime"
          ? "date"
          : meta?.type === "number" ||
              meta?.type === "currency" ||
              meta?.type === "percent"
            ? meta.type
            : "text";
      fields.push({
        id,
        label:
          meta?.label ?? (typeof def.header === "string" ? def.header : id),
        type,
        meta: type === "date" || type === "text" ? undefined : meta,
        accessor: accessorFn
          ? (row) => accessorFn(row, 0)
          : (row) => pathValue(row, accessorKey!),
      });
    });
  visit(columns);
  return fields;
};

export const usePivotView = <TData extends RowData>({
  pivot,
  columns,
  sourceRows,
  serverMode,
  stateKey,
  initialView,
  defaultColumn,
  search,
}: {
  pivot: TableV2PivotOptions<TData> | undefined;
  columns: TableV2ColumnDef<TData>[];
  sourceRows: TData[] | undefined;
  serverMode: boolean;
  stateKey?: string;
  initialView?: TableV2View;
  defaultColumn: object;
  search?: string;
}) => {
  const [view, setView] = useState<TableV2View>(() =>
    pivot ? (initialView ?? pivot.initialView ?? "table") : "table",
  );
  const showPivot = !!pivot && view === "pivot";

  const derivedFields = useMemo(
    () => pivot?.fields ?? fieldsFromColumns(columns),
    [pivot?.fields, columns],
  );

  const controller = usePivotController({
    fields: derivedFields,
    initialConfig: pivot?.initialConfig ?? EMPTY_CONFIG,
    data: serverMode ? undefined : sourceRows,
    fetchPivot: pivot?.fetchPivot,
    clientRowLimit: pivot?.clientRowLimit,
    initiallyExpanded: pivot?.initiallyExpanded,
    stateKey: stateKey ? `${stateKey}:pivot` : undefined,
    enabled: showPivot,
    search,
  });

  const live = useRef(controller);
  live.current = controller;

  const [detailCell, setDetailCell] = useState<PivotCellClick | null>(null);
  // `true` filters the table's own rows, which a server pivot doesn't have.
  const rowsOnServer =
    pivot?.drillToDetail === true && controller.mode === "server";
  const drill = rowsOnServer ? undefined : pivot?.drillToDetail;
  useEffect(() => {
    if (rowsOnServer)
      console.error(
        "TableV2: drillToDetail: true needs the rows on the client; pass a loader for server pivots.",
      );
  }, [rowsOnServer]);
  const cellClick = useRef<(cell: PivotCellClick) => void>(() => undefined);
  cellClick.current = (cell) => {
    pivot?.onCellClick?.(cell);
    if (drill) setDetailCell(cell);
  };
  const clickable = !!pivot?.onCellClick || !!drill;

  const { columns: pivotColumns, targets } = useMemo(
    () =>
      showPivot
        ? buildPivotColumns(
            controller,
            derivedFields,
            () => live.current,
            clickable ? (cell) => cellClick.current(cell) : undefined,
          )
        : { columns: NO_COLUMNS, targets: new Map() },
    [showPivot, controller.result, controller.config, derivedFields, clickable],
  );

  const sorting = useMemo(
    () => pivotSortingState(controller.config.sort),
    [controller.config.sort],
  );

  const pivotTable = useTable<TableV2Features, PivotRowNode>({
    features: tableV2Features,
    data: showPivot ? controller.result.rows : [],
    columns: pivotColumns,
    defaultColumn,
    getRowId: (row) => row.key,
    getSubRows: (row) => row.children,
    getRowCanExpand: (row) => row.original.hasChildren,
    enableRowSelection: false,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableSortingRemoval: false,
    columnResizeMode: "onChange",
    initialState: {
      columnPinning: { start: [PIVOT_LABEL_COLUMN_ID], end: [] },
    },
    state: { expanded: controller.expandedState, sorting },
    onExpandedChange: () => undefined,
    onSortingChange: (updater: Updater<SortingState>) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      controller.setSort(toPivotSort(next, targets));
    },
  });

  const detail: TableV2PivotDetail<TData> | null =
    detailCell && showPivot
      ? {
          cell: detailCell,
          close: () => setDetailCell(null),
          loadRows: () =>
            typeof drill === "function"
              ? drill(detailCell, controller.config)
              : Promise.resolve(
                  pivotDetailRows({
                    data: sourceRows ?? [],
                    fields: derivedFields,
                    config: controller.config,
                    row: detailCell.row,
                    column: detailCell.column,
                  }),
                ),
        }
      : null;

  return {
    view,
    setView,
    pivot: pivot
      ? ({
          controller,
          table: pivotTable,
          drillToDetail: !!drill,
          detail,
        } as TableV2PivotView<TData>)
      : null,
  };
};
